import type { AppState } from '@fcom/core/interfaces';
import type { Location } from '@fcom/core-api/interfaces';
import type { LocalDate } from '@fcom/core/utils';
import type { FormStatus } from '@fcom/service-forms';
import type { Attachment } from '@fcom/ui-components/components/interfaces';

import type { SpecialAssistanceFormFields } from './special-assistance-form.interface';

export enum FormType {
  FYI = 'fyi',
  FYA = 'fya',
  INDIVIDUAL = 'individual',
  REPRESENTATIVE = 'representative',
  CLAIM_COMPANY = 'claimCompany',
  MEDA = 'meda',
}

export enum CaseType {
  FYI = 'fyi',
  FYA = 'fya',
  MEDA = 'meda',
  AGENT = 'agent',
}

export enum FormCategoryInputs {
  FORM_CATEGORY = 'formCategory',
  FORM_SUB_CATEGORY = 'formSubCategory',
}

export enum FormCategories {
  FLIGHT_DISRUPTION = 'flightDisruption',
  BAGGAGE_IRREGULARITIES = 'baggageIrregularities',
  BOOKING_AND_TRAVEL_EXPERIENCE = 'bookingAndTravelExperience',
}

export enum FormSubCategories {
  DELAYED_OR_CANCELLED = 'delayedOrCancelled',
  OVERBOOKING = 'overbooking',
  DOWNGRADING = 'downgrading',
  DELAYED_BAGGAGE = 'delayedBaggage',
  DAMAGED_BAGGAGE = 'damagedBaggage',
  BOOKINGS = 'bookings',
  AIRPORT_EXPERIENCE = 'airportExperience',
  ONBOARD_EXPERIENCE = 'onboardExperience',
  TRAVEL_EXTRAS = 'travelExtras',
  SPECIAL_NEEDS = 'specialNeeds',
  FINNAIR_PLUS = 'finnairPlus',
}

export enum MultiStepFormSteps {
  FORM_TYPE = 'formType',
  CLAIMANT_DETAILS = 'claimantDetails',
  CATEGORY = 'category',
  BOOKING_INFORMATION = 'bookingInformation',
  ADD_PASSENGERS = 'addPassengers',
  DOWNGRADING = 'downgrading',
  EU_COMPENSATION = 'euCompensation',
  ADDITIONAL_EXPENSES = 'additionalExpenses',
  PAYMENT_DETAILS = 'paymentDetails',
  OPTIONAL_FEEDBACK = 'optionalFeedback',
  REVIEW_AND_SUBMIT = 'reviewAndSubmit',
  SECOND_CATEGORY = 'secondCategory',
  BAG_TRACING = 'bagTracing',
}

export enum SecondCategoryInput {
  SECOND_CATEGORY = 'secondCategory',
}

export enum BaggageOptions {
  BAGS = 'bags',
  PRAMS_BABY = 'pramsBaby',
  SPORT = 'sport',
  WHEELCHAIR = 'wheelChair',
  SPECIAL_BAGGAGE = 'specialBaggage',
  MISSING_ITEMS = 'missingItems',
}

export enum BookingOptions {
  TICKET_FARES_AND_RULES = 'ticketFaresAndRules',
  DIGITAL_SALES_SERVICES = 'digitalSalesServices',
  CUSTOMER_SERVICE_BY_FINNAIR = 'customerServiceByFinnair',
  CUSTOMER_SERVICE_BY_OTHER = 'customerServiceByOther',
  SCHEDULE_CHANGE = 'scheduleChange',
}

export enum AirportExperienceOptions {
  ARRIVAL_SERVICE = 'arrivalService',
  CHECK_IN = 'checkIn',
  GATE_SERVICE = 'gateService',
  GATEWAY_SERVICES = 'gatewayServices',
  LOUNGES = 'lounges',
  DIGITAL_GROUND_SERVICES = 'digitalGroundServices',
  DENIED_ACCEPTANCE = 'deniedAcceptance',
  AUTHORITIES_AND_OTHER_SERVICES = 'authoritiesAndOtherServices',
  PERSONAL_INJURY = 'personalInjury',
}

export enum OnboardExperienceOptions {
  CREW = 'crew',
  FOOD_AND_DRINK = 'foodAndDrink',
  INFLIGHT_ENTERTAINMENT = 'inflightEntertainment',
  SEATS_AND_SEATING = 'seatsAndSeating',
  INTERNET = 'internet',
  CABIN_EXPERIENCE = 'cabinExperience',
}

export enum TravelExtrasOptions {
  SEATS = 'seats',
  MEALS = 'meals',
  BAGGAGE = 'baggage',
  UPGRADES = 'upgrades',
  WIFI = 'wifi',
  TREATS_AND_COMFORT_ITEMS = 'treatsAndComfortItems',
  ADVERTISING = 'advertising',
}

export enum SpecialNeedsOptions {
  CUSTOMER_WITH_DISABILITY = 'customerWithDisability',
  CUSTOMER_WITH_MEDICAL_CONDITION = 'customerWithMedicalCondition',
  ELDERLY_CUSTOMER = 'elderlyCustomer',
  TRAVELING_WITH_SMALL_CHILDREN = 'travelingWithSmallChildren',
  UNACCOMPANIED_MINOR = 'unaccompaniedMinor',
  WHEELCHAIR = 'wheelChair',
  PET_IN_CABIN_OR_IN_HOLD = 'petInCabinOrInHold',
}

export enum FinnairPlusOptions {
  SERVICE = 'service',
  MEMBER_SHIP_BENEFIT = 'memberShipBenefit',
  AWARDS = 'awards',
  MARKETING = 'marketing',
  RULES_AND_CONDITIONS = 'rulesAndConditions',
}

export type SecondCategoryOption =
  | BaggageOptions
  | BookingOptions
  | AirportExperienceOptions
  | OnboardExperienceOptions
  | TravelExtrasOptions
  | SpecialNeedsOptions
  | FinnairPlusOptions;

export enum BagTracingInputs {
  HAS_WORLD_TRACER_NUMBER = 'hasWorldTracerNumber',
  WORLD_TRACER_NUMBER = 'worldTracerNumber',
}

export enum OptionalFeedbackInputs {
  GIVE_FEEDBACK = 'giveFeedback',
  ADDITIONAL_COMMENTS = 'additionalComments',

  CARE = 'care',
  REROUTING = 'reroutingRebooking',
  CUSTOMER_SERVICE_ATTITUDE = 'customerServiceAttitude',
  REACHABILITY = 'reachability',
  DIGITAL_SERVICES = 'digitalServices',
  INFORMATION = 'information',
  CUSTOMER_COMMUNICATION = 'customerCommunication',
  AVAILABILITY_OF_FARES = 'availabilityOfFares',
  NO_SHOW_OR_RETURN_CANCELLED = 'noShowOrReturnCancelled',
  FARE_RESTRICTIONS = 'fareRestrictions',
  REFUND_POLICY = 'refundPolicy',
  WEBSITE_FUNCTIONALITY = 'websiteFunctionality',
  FINNAIR_APP = 'finnairApp',
  CHAT = 'chat',
  SOCIAL_MEDIA = 'socialMedia',
  WHATSAPP = 'whatsapp',
  REACHABILITY_OF_CONTACT_CENTER = 'reachabilityOfContactCenter',
  CUSTOMER_SERVICE_SKILLS = 'customerServiceSkills',
  TRAVEL_AGENCIES = 'travelAgencies',
  OTHER_DISTRIBUTORS = 'otherDistributors',
  CHANGES_IN_TRAVEL_PLANS = 'changesInTravelPlans',
  CHANGES_NOT_INFORMED = 'changesNotInformed',
  LONG_BAGGAGE_DELIVERY_TIME = 'longBaggageDeliveryTime',
  INFORMATION_ANNOUNCEMENTS_SIGNAGE = 'informationAnnouncementsSignage',
  ARRIVAL_SERVICE_REACHABALITY_BY_PHONE = 'arrivalServiceReachabalityByPhone',
  EXCESS_BAGGAGE_CHARGE = 'excessBaggageCharge',
  PRE_PAID_SEATS = 'prePaidSeats',
  SEATING = 'seating',
  CHECK_IN_QUEUE = 'checkInQueue',
  RECOGNITION_OF_TOP_TIER_PRIORITY_CUSTOMER = 'recognitionOfTopTierPriorityCustomer',
  FLUENCY_OF_BOARDING = 'fluencyOfBoarding',
  CARRY_ON_BAGGAGE = 'carryOnBaggage',
  REFUSED_ACCEPTANCE_AT_THE_BOARDING_GATE = 'refusedAcceptanceAtTheBoardingGate',
  MISSED_CONNECTING_FLIGHT = 'missedConnectingFlight',
  ACCESS_TO_LOUNGE = 'accessToLounge',
  INTERIOR_APPEARANCE_CLEANLINESS = 'interiorAppearanceCleanliness',
  MEAL_AND_BEVERAGE_SERVICE = 'mealAndBeverageService',
  INTERNET_CHECK_IN = 'internetCheckIn',
  KIOSK_CHECK_IN = 'kioskCheckIn',
  SMS_CHECK_IN = 'smsCheckIn',
  INFO_ON_DIGITAL_CHANNEL = 'infoOnDigitalChannel',
  TRAVEL_DOCUMENT_ISSUE = 'travelDocumentIssue',
  REFUSED_ACCEPTANCE_AT_CHECK_IN = 'refusedAcceptanceAtCheckIn',
  CUSTOMS = 'customs',
  PASSPORT_CONTROL = 'passportControl',
  SECURITY_CHECKS = 'securityChecks',
  FACILITIES_OR_SIGNAGE_OR_SHOPS = 'facilitiesOrSignageOrShops',
  FLY_RAIL_TRAIN_CONNECTION_IN_GERMANY = 'flyRailTrainConnectionInGermany',
  INJURY_HAPPENED_INSIDE_AIRPORT_TERMINAL = 'injuryHappenedInsideAirportTerminal',
  INJURY_HAPPENED_WHEN_BOARDING_EXITING_THE_AIRCRAFT = 'injuryHappenedWhenBoardingExitingTheAircraft',
  DAMAGE_SPILLAGE_CAUSED_TO_CLOTHING_OR_BELONGINGS = 'damageSpillageCausedToClothingOrBelongings',
  PILOTS = 'pilots',
  COMPLIMENTARY_MEALS = 'complimentaryMeals',
  SPECIAL_MEALS = 'specialMeals',
  FOOD_AND_DRINK_BOUGHT_ONBOARD = 'foodAndDrinkBoughtOnboard',
  FOOD_SAFETY = 'foodSafety',
  DEFECTED_WORKING_ONLY_PARTIALLY = 'defectedWorkingOnlyPartially',
  SELECTION_OF_MOVIES_GAMES_MUSIC_NEWSPAPERS = 'selectionOfMoviesGamesMusicNewspapers',
  DEFECTIVE_SEAT = 'defectiveSeat',
  DEFECTIVE_TABLE_OR_READING_LIGHT_ETC = 'defectiveTableOrReadingLightEtc',
  INTERNET_NOT_WORKING = 'internetNotWorking',
  CLEANLINESS_AND_HYGIENE = 'cleanlinessAndHygiene',
  HEATING_VENTILATION_NOISE_LIGHTS = 'heatingVentilationNoiseLights',
  AIRCRAFT_TYPE_CODE_SHARE = 'aircraftTypeCodeShare',
  HAND_BAGGAGE_ISSUES = 'handBaggageIssues',
  DISTURBING_FELLOW_PASSENGER = 'disturbingFellowPassenger',
  AVAILABILITY = 'availability',
  SEAT_CHANGED = 'seatChanged',
  BOOKING_PROCESS = 'bookingProcess',
  PRICE = 'price',
  SELECTION = 'selection',
  MISSING_OR_CHANGED_MEAL = 'missingOrChangedMeal',
  QUALITY = 'quality',
  WIFI_NOT_WORKING = 'wifiNotWorking',
  ITEM_MISSING_OR_BROKEN = 'itemMissingOrBroken',
  INFLIGHT_PREORDER_SALES = 'inflightPreorderSales',
  BOOKING_EXPERIENCE = 'bookingExperience',
  AIRPORT_EXPERIENCE = 'airportExperience',
  INFLIGHT_EXPERIENCE = 'inflightExperience',
  DAMAGED_MOBILITY_AID = 'damagedMobilityAid',
  DELAYED_MOBILITY_AID = 'delayedMobilityAid',
  MOBILITY_AID_HANDLING = 'mobilityAidHandling',
  DELAYED_DELIVERY_OF_PET = 'delayedDeliveryOfPet',
  DAMAGED_PET_CARRIER = 'damagedPetCarrier',
  WEB_PAGES_FUNCTIONALITY_INFO = 'webPagesFunctionalityInfo',
  FUNCTIONALITY_OF_PROCEDURES = 'functionalityOfProcedures',
  REACHABILITY_OF_SERVICE = 'reachabilityOfService',
  TIER_MEMBER_BENEFITS = 'tierMemberBenefits',
  BASIC_MEMBER_BENEFITS = 'basicMemberBenefits',
  JUNIOR_PLUS_BENEFITS = 'juniorPlusBenefits',
  UPGRADES = 'upgrades',
  FLIGHT_AWARD_AVAILABILITY = 'flightAwardAvailability',
  FINNAIR_FLIGHT_AWARD = 'finnairFlightAward',
  PARTNER_FLIGHT_AWARD = 'partnerFlightAward',
  NON_AIRLINE_AWARD = 'nonAirlineAward',
  CAMPAIGN = 'campaign',
  EMAIL_NEWSLETTERS = 'emailNewsletters',
  PRINTED_MATERIAL = 'printedMaterial',
  MEMBERSHIP_CARDS = 'membershipCards',
  TIER_QUALIFICATION = 'tierQualification',
  EXPIRATION_OF_POINTS = 'expirationOfPoints',
  COMPARISON_TO_COMPETITOR_PROGRAM = 'comparisonToCompetitorProgram',
  JOINING_FINNAIR_PLUS = 'joiningFinnairPlus',
}

export type OptionalFeedbackOptions = Record<OptionalFeedbackInputs, string>;

export enum CustomerFields {
  MEMBER_NUMBER = 'memberNumber',
}

export enum FeedbackRateOptions {
  VERY_POOR = 'veryPoor',
  POOR = 'poor',
  OKAY = 'okay',
  GOOD = 'good',
  EXCELLENT = 'excellent',
}

export enum GiveOptionalFeedbackOptions {
  YES = 'addFeedback',
  NO = 'plainClaim',
}

export enum ClaimantDetailInputs {
  AUTHORIZED_REP = 'authorizedRep',
  AUTHORIZED_COMPANY = 'authorizedCompany',
  COMPANY_TYPE = 'companyType',
  CLAIM_COMPANY = 'claimCompany',
  CLAIM_COMPANY_OTHER = 'claimCompanyOther',
  REFERENCE_NUMBER = 'referenceNumber',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  PHONE = 'phone',
}

export enum CompanyType {
  CLAIM_COMPANY = 'claimCompany',
  TRAVEL_AGENT_OR_OPERATOR = 'travelAgentOrOperator',
  INSURANCE_COMPANY = 'insuranceCompany',
  OTHER = 'other',
}

export enum BookingInfoInputs {
  RESERVATION_NUMBER = 'reservationNumber',
  FLIGHT_DATE = 'flightDate',
  FLIGHT_NUMBER = 'flightNumber',
  ORIGIN = 'origin',
  DESTINATION = 'destination',
  FLIGHT_LEG = 'flightLeg',
}

export enum PaymentDetailsInputs {
  COMPENSATION_TYPE = 'compensationType',
  IBAN = 'iban',
  ACCOUNT_NUMBER = 'accountNumber',
  BIC = 'bic',
  ACCOUNT_HOLDER = 'accountHolder',
  ACCOUNT_HOLDER_ADDRESS = 'accountHolderAddress',
  BANK_NAME = 'bankName',
  BANK_ADDRESS = 'bankAddress',
  BANK_COUNTRY = 'country',
  NCC = 'ncc',
  IFSC = 'ifsc',
  BIK = 'bik',
  CBD = 'correspondentBankData',
  KPP = 'kpp',
  INN = 'inn',
  PATRONYMIC_NAME = 'patronymicName',
}

export enum CompensationTypeOptions {
  GIFT_VOUCHER = 'voucher',
  CASH = 'cash',
}

export enum AddPassengersInputs {
  MAIN_PASSENGER = 'mainPassenger',
  COMPANIONS = 'companions',
}

export enum PassengerInputs {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  PHONE = 'phone',
  COUNTRY = 'country',
  IS_CHILD = 'isChild',
  ATTACHMENT = 'attachment',
}

export enum EuCompensationInputs {
  CLAIM_TYPE = 'claimType',
  EU_CLAIM_OPTION = 'euClaimOption',
}

export enum ClaimType {
  EU_STANDARD_COMPENSATION = 'euStandardCompensation',
  RECEIVED_COMPENSATION = 'receivedCompensation',
  SPECIAL_CASE_COMPENSATION = 'specialCaseCompensation',
  JUST_FEEDBACK = 'justFeedback',
}

export enum PassengerInfoType {
  SELF = 'self',
  OTHERS = 'others',
}

export enum EUClaimOptions {
  SHORT = 'short',
  SHORT_HALF = 'shortHalf',
  MIDDLE = 'middle',
  MIDDLE_HALF = 'middleHalf',
  LONG = 'long',
  LONG_HALF = 'longHalf',
}

export enum DowngradingOptions {
  CABIN = 'cabin',
  ADDITIONAL_EXPENSES = 'additionalExpenses',
  TRAVEL_EXTRAS = 'travelExtras',
}

export enum DowngradingInputs {
  DOWNGRADING_CLAIM_TYPE = 'downgradingClaimType',
}

export enum AdditionalExpensesInputs {
  ADDITIONAL_EXPENSES = 'additionalExpenses',
  HOTEL_COST = 'hotelCosts',
  TRANSPORT_COST = 'transportCosts',
  MEAL_COST = 'mealCosts',
  PHONE_COST = 'phoneCosts',
  CLOTH_AND_COSMETIC_COST = 'clothAndCosmeticCosts',
  RENTED_COST = 'rentedCost',
  OTHER_COST = 'otherCosts',
  INDIRECT_COST = 'indirectCosts',
  INDIRECT_COST_SPECIFICATION = 'indirectCostSpecification',
  ATTACHMENTS = 'attachments',
}

export enum AdditionalExpenses {
  YES = 'yes',
  NO = 'no',
}

export enum ReviewAndSubmitInputs {
  CONFIRM = 'confirm',
}

interface Passenger {
  [PassengerInputs.FIRST_NAME]: string;
  [PassengerInputs.LAST_NAME]: string;
}

export interface MainPassenger extends Passenger {
  [PassengerInputs.COUNTRY]: string;
  [PassengerInputs.EMAIL]: string;
  [PassengerInputs.PHONE]: string;
  [PassengerInputs.ATTACHMENT]?: Attachment[];
}

export interface Companion extends Passenger {
  [PassengerInputs.EMAIL]?: string;
  [PassengerInputs.PHONE]?: string;
  [PassengerInputs.ATTACHMENT]?: Attachment[];
  [PassengerInputs.IS_CHILD]: boolean;
}

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

export interface SpecialAssistance {
  ableToSitWithKneesBent: boolean;
  additionalInformation: string;
  additionalMedicalEquipment: string;
  assistantName: string;
  assistanceType: string;
  capapDeviceModel: string;
  reasonForWheelchair: string;
  travellingWithAssistant: boolean;
  typeOfMedicalEquipment: string;
  typeOfCondition: string;
  otherReasonForAssistance: string;
  ownWheelchair: boolean;
  otherMedicalEquipmentModel: string;
  otherMedicalEquipmentType: string;
  portableOxygenConcentratorModel: string;
  wheelchairDepth: number;
  wheelchairHeight: number;
  wheelchairWidth: number;
  wheelchairWeight: number;
  wheelchairType: string;
  [SpecialAssistanceFormFields.MEDA_ATTACHMENT]?: Attachment[];
  amountOfLithiumIonBatteries?: string | number;
  battery1Power?: string;
  battery2Power?: string;
  otherMobilityAidDescription?: string;
}

export interface CaseFormData {
  formType: FormType;
  customerFields?: {
    [CustomerFields.MEMBER_NUMBER]: string;
  };
  [MultiStepFormSteps.CLAIMANT_DETAILS]?: {
    [ClaimantDetailInputs.COMPANY_TYPE]?: string;
    [ClaimantDetailInputs.CLAIM_COMPANY]?: string;
    [ClaimantDetailInputs.CLAIM_COMPANY_OTHER]?: string;
    [ClaimantDetailInputs.FIRST_NAME]?: string;
    [ClaimantDetailInputs.LAST_NAME]?: string;
    [ClaimantDetailInputs.EMAIL]: string;
    [ClaimantDetailInputs.PHONE]: string;
    [ClaimantDetailInputs.REFERENCE_NUMBER]?: string;
  };
  [MultiStepFormSteps.CATEGORY]: {
    [FormCategoryInputs.FORM_CATEGORY]: string;
    [FormCategoryInputs.FORM_SUB_CATEGORY]: string;
  };
  [MultiStepFormSteps.BOOKING_INFORMATION]: {
    [BookingInfoInputs.RESERVATION_NUMBER]: string;
    [BookingInfoInputs.FLIGHT_DATE]: LocalDate;
    [BookingInfoInputs.FLIGHT_NUMBER]: string;
    [BookingInfoInputs.ORIGIN]: Location;
    [BookingInfoInputs.DESTINATION]: Location;
  };
  [MultiStepFormSteps.ADD_PASSENGERS]: {
    [AddPassengersInputs.MAIN_PASSENGER]: MainPassenger;
    [AddPassengersInputs.COMPANIONS]: Companion[];
  };
  [MultiStepFormSteps.OPTIONAL_FEEDBACK]: Omit<
    PartialRecord<OptionalFeedbackInputs, GiveOptionalFeedbackOptions | boolean>,
    'additionalComments'
  > & {
    additionalComments?: string;
    customerCommunicationRate?: FeedbackRateOptions;
    careRate?: FeedbackRateOptions;
    reroutingRebookingRate?: FeedbackRateOptions;
    customerServiceRate?: FeedbackRateOptions;
    reachabilityRate?: FeedbackRateOptions;
    digitalServicesRate?: FeedbackRateOptions;
    informationRate?: FeedbackRateOptions;
    availabilityOfFaresRate?: FeedbackRateOptions;
    noShowOrReturnCancelledRate?: FeedbackRateOptions;
    fareRestrictionsRate?: FeedbackRateOptions;
    refundPolicyRate?: FeedbackRateOptions;
    websiteFunctionalityRate?: FeedbackRateOptions;
    finnairAppRate?: FeedbackRateOptions;
    chatRate?: FeedbackRateOptions;
    socialMediaRate?: FeedbackRateOptions;
    whatsappRate?: FeedbackRateOptions;
    reachabilityOfContactCenterRate?: FeedbackRateOptions;
    customerServiceSkillsRate?: FeedbackRateOptions;
    customerServiceAttitudeAndFriendlinessRate?: FeedbackRateOptions;
    travelAgenciesRate?: FeedbackRateOptions;
    otherDistributorsRate?: FeedbackRateOptions;
    changesInTravelPlansRate?: FeedbackRateOptions;
    changesNotInformedRate?: FeedbackRateOptions;
    longBaggageDeliveryTimeRate?: FeedbackRateOptions;
    informationAnnouncementsSignageRate?: FeedbackRateOptions;
    arrivalServiceReachabalityByPhoneRate?: FeedbackRateOptions;
    excessBaggageChargeRate?: FeedbackRateOptions;
    prePaidSeatsRate?: FeedbackRateOptions;
    seatingRate?: FeedbackRateOptions;
    checkInQueueRate?: FeedbackRateOptions;
    recognitionOfTopTierPriorityCustomerRate?: FeedbackRateOptions;
    fluencyOfBoardingRate?: FeedbackRateOptions;
    carryOnBaggageRate?: FeedbackRateOptions;
    refusedAcceptanceAtTheBoardingGateRate?: FeedbackRateOptions;
    missedConnectingFlightRate?: FeedbackRateOptions;
    accessToLoungeRate?: FeedbackRateOptions;
    interiorAppearanceCleanlinessRate?: FeedbackRateOptions;
    mealAndBeverageServiceRate?: FeedbackRateOptions;
    internetCheckInRate?: FeedbackRateOptions;
    kioskCheckInRate?: FeedbackRateOptions;
    smsCheckInRate?: FeedbackRateOptions;
    infoOnDigitalChannelRate?: FeedbackRateOptions;
    travelDocumentIssueRate?: FeedbackRateOptions;
    refusedAcceptanceAtCheckInRate?: FeedbackRateOptions;
    customsRate?: FeedbackRateOptions;
    passportControlRate?: FeedbackRateOptions;
    securityChecksRate?: FeedbackRateOptions;
    facilitiesOrSignageOrShopsRate?: FeedbackRateOptions;
    flyRailTrainConnectionInGermanyRate?: FeedbackRateOptions;
    injuryHappenedInsideAirportTerminalRate?: FeedbackRateOptions;
    injuryHappenedWhenBoardingExitingTheAircraftRate?: FeedbackRateOptions;
    damageSpillageCausedToClothingOrBelongingsRate?: FeedbackRateOptions;
    pilotsRate?: FeedbackRateOptions;
    complimentaryMealsRate?: FeedbackRateOptions;
    specialMealsRate?: FeedbackRateOptions;
    foodAndDrinkBoughtOnboardRate?: FeedbackRateOptions;
    foodSafetyRate?: FeedbackRateOptions;
    defectedWorkingOnlyPartiallyRate?: FeedbackRateOptions;
    selectionOfMoviesGamesMusicNewspapersRate?: FeedbackRateOptions;
    defectiveSeatRate?: FeedbackRateOptions;
    defectiveTableOrReadingLightEtcRate?: FeedbackRateOptions;
    internetNotWorkingRate?: FeedbackRateOptions;
    cleanlinessAndHygieneRate?: FeedbackRateOptions;
    heatingVentilationNoiseLightsRate?: FeedbackRateOptions;
    aircraftTypeCodeShareRate?: FeedbackRateOptions;
    handBaggageIssuesRate?: FeedbackRateOptions;
    disturbingFellowPassengerRate?: FeedbackRateOptions;
    availabilityRate?: FeedbackRateOptions;
    seatChangedRate?: FeedbackRateOptions;
    bookingProcessRate?: FeedbackRateOptions;
    priceRate?: FeedbackRateOptions;
    selectionRate?: FeedbackRateOptions;
    missingOrChangedMealRate?: FeedbackRateOptions;
    qualityRate?: FeedbackRateOptions;
    wifiNotWorkingRate?: FeedbackRateOptions;
    itemMissingOrBrokenRate?: FeedbackRateOptions;
    inflightPreorderSalesRate?: FeedbackRateOptions;
    bookingExperienceRate?: FeedbackRateOptions;
    airportExperienceRate?: FeedbackRateOptions;
    inflightExperienceRate?: FeedbackRateOptions;
    damagedMobilityAidRate?: FeedbackRateOptions;
    delayedMobilityAidRate?: FeedbackRateOptions;
    mobilityAidHandlingRate?: FeedbackRateOptions;
    delayedDeliveryOfPetRate?: FeedbackRateOptions;
    damagedPetCarrierRate?: FeedbackRateOptions;
    webPagesFunctionalityInfoRate?: FeedbackRateOptions;
    functionalityOfProceduresRate?: FeedbackRateOptions;
    reachabilityOfServiceRate?: FeedbackRateOptions;
    tierMemberBenefitsRate?: FeedbackRateOptions;
    basicMemberBenefitsRate?: FeedbackRateOptions;
    juniorPlusBenefitsRate?: FeedbackRateOptions;
    upgradesRate?: FeedbackRateOptions;
    flightAwardAvailabilityRate?: FeedbackRateOptions;
    finnairFlightAwardRate?: FeedbackRateOptions;
    partnerFlightAwardRate?: FeedbackRateOptions;
    nonAirlineAwardRate?: FeedbackRateOptions;
    campaignRate?: FeedbackRateOptions;
    emailNewslettersRate?: FeedbackRateOptions;
    printedMaterialRate?: FeedbackRateOptions;
    membershipCardsRate?: FeedbackRateOptions;
    tierQualificationRate?: FeedbackRateOptions;
    expirationOfPointsRate?: FeedbackRateOptions;
    comparisonToCompetitorProgramRate?: FeedbackRateOptions;
    joiningFinnairPlusRate?: FeedbackRateOptions;
  };
  [MultiStepFormSteps.PAYMENT_DETAILS]?: {
    [PaymentDetailsInputs.COMPENSATION_TYPE]: CompensationTypeOptions;
    [PaymentDetailsInputs.IBAN]?: string;
    [PaymentDetailsInputs.BIC]?: string;
    [PaymentDetailsInputs.ACCOUNT_HOLDER]?: string;
    [PaymentDetailsInputs.ACCOUNT_HOLDER_ADDRESS]?: string;
    [PaymentDetailsInputs.BANK_NAME]?: string;
    [PaymentDetailsInputs.BANK_ADDRESS]?: string;
    [PaymentDetailsInputs.BANK_COUNTRY]?: string;
    [PaymentDetailsInputs.ACCOUNT_NUMBER]?: string;
    [PaymentDetailsInputs.NCC]?: string;
    [PaymentDetailsInputs.IFSC]?: string;
    [PaymentDetailsInputs.BIK]?: string;
    [PaymentDetailsInputs.CBD]?: string;
    [PaymentDetailsInputs.KPP]?: string;
    [PaymentDetailsInputs.INN]?: string;
    [PaymentDetailsInputs.PATRONYMIC_NAME]?: string;
  };
  [MultiStepFormSteps.SECOND_CATEGORY]?: {
    [SecondCategoryInput.SECOND_CATEGORY]: SecondCategoryOption;
  };
  [MultiStepFormSteps.EU_COMPENSATION]?: {
    [EuCompensationInputs.CLAIM_TYPE]: ClaimType;
    [EuCompensationInputs.EU_CLAIM_OPTION]: EUClaimOptions;
    [AdditionalExpensesInputs.OTHER_COST]?: boolean;
    [AdditionalExpensesInputs.INDIRECT_COST]?: string;
    [AdditionalExpensesInputs.INDIRECT_COST_SPECIFICATION]?: string;
    [AdditionalExpensesInputs.ATTACHMENTS]?: Attachment[];
    [AdditionalExpensesInputs.CLOTH_AND_COSMETIC_COST]?: string;
    [AdditionalExpensesInputs.RENTED_COST]?: string;
    [AdditionalExpensesInputs.HOTEL_COST]?: string;
    [AdditionalExpensesInputs.TRANSPORT_COST]?: string;
    [AdditionalExpensesInputs.MEAL_COST]?: string;
    [AdditionalExpensesInputs.PHONE_COST]?: string;
    careRate?: FeedbackRateOptions;
    reroutingRebookingRate?: FeedbackRateOptions;
    customerServiceAttitudeAndFriendlinessRate?: FeedbackRateOptions;
    reachabilityRate?: FeedbackRateOptions;
    digitalServicesRate?: FeedbackRateOptions;
    informationRate?: FeedbackRateOptions;
    additionalComments?: string;
  };
  [MultiStepFormSteps.ADDITIONAL_EXPENSES]?: {
    [AdditionalExpensesInputs.ADDITIONAL_EXPENSES]: string;
    [AdditionalExpensesInputs.OTHER_COST]: boolean;
    [AdditionalExpensesInputs.INDIRECT_COST]?: string;
    [AdditionalExpensesInputs.INDIRECT_COST_SPECIFICATION]?: string;
    [AdditionalExpensesInputs.ATTACHMENTS]?: Attachment[];
    [AdditionalExpensesInputs.CLOTH_AND_COSMETIC_COST]?: string;
    [AdditionalExpensesInputs.RENTED_COST]?: string;
    [AdditionalExpensesInputs.HOTEL_COST]?: string;
    [AdditionalExpensesInputs.TRANSPORT_COST]?: string;
    [AdditionalExpensesInputs.MEAL_COST]?: string;
    [AdditionalExpensesInputs.PHONE_COST]?: string;
  };
  [MultiStepFormSteps.BAG_TRACING]?: {
    [BagTracingInputs.HAS_WORLD_TRACER_NUMBER]: boolean;
    [BagTracingInputs.WORLD_TRACER_NUMBER]: string;
  };
  specialAssistance: SpecialAssistance;
}

export interface BankAccount {
  accountHolder: string;
  accountHolderAddress: string;
  bankName: string;
  bankAddress: string;
  bankCountry: string;
  iban: string;
  bic: string;
  bik: string;
  ncc: string;
  correspondentBankData: string;
  patronymicName: string;
  ifsc: string;
  inn: string;
  kpp: string;
}

interface BaseCosts {
  additionalExpenses: number;
  additionalExpensesDescription: string;
}

export interface EuCompensationCosts extends BaseCosts {
  hotelCosts: number;
  mealCosts: number;
  transportationCosts: number;
  phoneCosts: number;
  standardCompensation: number;
}

export interface DelayedBaggageCosts extends BaseCosts {
  bagItemCosts: number;
  bagRentalCosts: number;
}

export type Rating = 1 | 2 | 3 | 4 | 5;

export interface FeedbackRating {
  name: string;
  rating: Rating;
}

export interface CaseCategory {
  name: string;
  child: {
    name: string;
    children?: FeedbackRating[];
  };
}

export interface FormCase {
  caseType: CaseType;
  caseOrigin: 'portal';
  country: string;
  language: string;
  freeTextFeedback: string;
  bookingReference?: string;
  worldTracerNumber?: string;
  flightDate?: string;
  flightNumber?: string;
  origin?: string;
  destination?: string;
  compensationType?: 'voucher' | 'cash';
  claimCompany?: CaseCompany;
  creator?: CaseCreator;
  customer: CaseCustomer;
  companions: CasePassengerDetails[];
  bankAccount?: BankAccount;
  euCompensationCosts?: EuCompensationCosts;
  delayedBaggageCosts?: DelayedBaggageCosts;
  specialAssistance?: SpecialAssistance;
  category: CaseCategory;
}

export interface CasePostMessage {
  caseId: string;
  lastName: string;
  message: {
    content: string;
    attachmentIds?: string[];
  };
}

export enum MessageCreator {
  CUSTOMER = 'customer',
  FINNAIR = 'finnair',
}

export interface MessageAttachment {
  attachmentId: string;
  fileName: string;
  hash: string;
}

export interface CaseGetMessage {
  content: string;
  creationDate: string;
  creator: MessageCreator;
  attachments: MessageAttachment[];
}

export enum CaseStatus {
  NEW = 'new',
  HANDLING = 'handling',
  WAITING = 'waiting',
  HANDLED = 'handled',
}

// TODO: align with the real case model FormCase
export interface RetrieveCase extends FormCase {
  caseId: string;
  status: CaseStatus;
  creationDate: string;
  requestReason: string;
  caseSFId: string;
  messages: CaseGetMessage[];
}

export interface CaseSubmissionData {
  case: FormCase;
  attachmentIds: string[];
}

export interface CaseCustomer extends CasePassengerDetails {
  fPlusNumber: string;
  countryOfResidence: string;
}

export type CaseCreator = CasePassengerDetails;

export interface CaseCompany {
  companyType: string;
  name: string;
  email: string;
  phone: string;
  reference?: string;
}

export interface CasePassengerDetails {
  firstname: string;
  lastname: string;
  email?: string;
  phone?: string;
}

export interface CaseAttachment {
  file: File;
  fileId?: string;
  status: FormStatus;
  error?: string;
}

export interface MultiStepFormState extends AppState {
  readonly formSteps: MultiStepFormSteps[];
}

export const MedaCategories = {
  /** Custom for meda **/
  wheelChairOrMobA: 'Wheelchair or other mobility aid',
  hearingImpaired: 'Hearing impaired',
  visuallyImpaired: 'Visually impaired',
  visuallyImpairedWithGuideDog: 'Visually impaired travelling with a dog',
  travelingWithServiceDog: 'Traveling with a service dog',
  pregnant: 'Pregnancy',
  medicalCondition: 'Medical condition',
  medicalEquipment: 'Medical equipment',
  other: 'Other condition',
};

export const CategoryMapLevel1 = {
  [FormSubCategories.DELAYED_OR_CANCELLED]: 'Flight irregularities',
  [FormSubCategories.OVERBOOKING]: 'Overbooking/Downgrading',
  [FormSubCategories.DOWNGRADING]: 'Overbooking/Downgrading',
  [FormSubCategories.DELAYED_BAGGAGE]: 'Delayed baggage',
  [FormSubCategories.DAMAGED_BAGGAGE]: 'Damaged baggage',
  [FormSubCategories.BOOKINGS]: 'Sales',
  [FormSubCategories.AIRPORT_EXPERIENCE]: 'Ground experience',
  [FormSubCategories.ONBOARD_EXPERIENCE]: 'Inflight experience',
  [FormSubCategories.TRAVEL_EXTRAS]: 'Travel Extras',
  [FormSubCategories.SPECIAL_NEEDS]: 'Customers with special needs',
  [FormSubCategories.FINNAIR_PLUS]: 'Finnair Plus',
  ...MedaCategories,
};

export const CategoryMapLevel2 = {
  [FormSubCategories.DELAYED_OR_CANCELLED]: 'Handling during irregularity',
  [FormSubCategories.OVERBOOKING]: 'Overbooking',
  [FormSubCategories.DOWNGRADING]: 'Downgrading',
  [BaggageOptions.BAGS]: 'Bags',
  [BaggageOptions.PRAMS_BABY]: 'Prams/baby equipment',
  [BaggageOptions.SPORT]: 'Sports equipment',
  [BaggageOptions.WHEELCHAIR]: 'Wheelchair and mobility aid',
  [BaggageOptions.SPECIAL_BAGGAGE]: 'Special baggage',
  [BaggageOptions.MISSING_ITEMS]: 'Missing items',
  [BookingOptions.TICKET_FARES_AND_RULES]: 'Ticket fares and rules',
  [BookingOptions.DIGITAL_SALES_SERVICES]: 'Digital sales services',
  [BookingOptions.CUSTOMER_SERVICE_BY_FINNAIR]: 'Customer service by Finnair',
  [BookingOptions.CUSTOMER_SERVICE_BY_OTHER]: 'Customer service by other suppliers',
  [BookingOptions.SCHEDULE_CHANGE]: 'Schedule change',
  [AirportExperienceOptions.ARRIVAL_SERVICE]: 'Arrival service',
  [AirportExperienceOptions.CHECK_IN]: 'Check-in',
  [AirportExperienceOptions.GATE_SERVICE]: 'Gate service',
  [AirportExperienceOptions.GATEWAY_SERVICES]: 'Gateway/Transfer desk services',
  [AirportExperienceOptions.LOUNGES]: 'Lounges',
  [AirportExperienceOptions.DIGITAL_GROUND_SERVICES]: 'Digital ground services',
  [AirportExperienceOptions.DENIED_ACCEPTANCE]: 'Denied acceptance on a flight (other than overbooking)',
  [AirportExperienceOptions.AUTHORITIES_AND_OTHER_SERVICES]: 'Authorities and other services at airport (not Finnair)',
  [AirportExperienceOptions.PERSONAL_INJURY]: 'Personal injury',
  [OnboardExperienceOptions.CREW]: 'Crew',
  [OnboardExperienceOptions.FOOD_AND_DRINK]: 'Food & Drink',
  [OnboardExperienceOptions.INFLIGHT_ENTERTAINMENT]: 'Inflight entertainment',
  [OnboardExperienceOptions.SEATS_AND_SEATING]: 'Seat & Seating',
  [OnboardExperienceOptions.INTERNET]: 'Internet',
  [OnboardExperienceOptions.CABIN_EXPERIENCE]: 'Cabin experience',
  [TravelExtrasOptions.SEATS]: 'Seats',
  [TravelExtrasOptions.MEALS]: 'Meals',
  [TravelExtrasOptions.BAGGAGE]: 'Baggage',
  [TravelExtrasOptions.UPGRADES]: 'Upgrades',
  [TravelExtrasOptions.WIFI]: 'Wifi',
  [TravelExtrasOptions.TREATS_AND_COMFORT_ITEMS]: 'Travel retail',
  [TravelExtrasOptions.ADVERTISING]: 'Advertising',
  [SpecialNeedsOptions.CUSTOMER_WITH_DISABILITY]: 'Customers with disabilities',
  [SpecialNeedsOptions.CUSTOMER_WITH_MEDICAL_CONDITION]: 'Medical condition',
  [SpecialNeedsOptions.ELDERLY_CUSTOMER]: 'Elderly customers',
  [SpecialNeedsOptions.TRAVELING_WITH_SMALL_CHILDREN]: 'Families with small children',
  [SpecialNeedsOptions.UNACCOMPANIED_MINOR]: 'Unaccompanied minors',
  [SpecialNeedsOptions.PET_IN_CABIN_OR_IN_HOLD]: 'Pet in cabin (PETC) or pet in hold (AVIH)',
  [FinnairPlusOptions.SERVICE]: 'Finnair Plus service',
  [FinnairPlusOptions.MEMBER_SHIP_BENEFIT]: 'Finnair Plus membership benefit',
  [FinnairPlusOptions.AWARDS]: 'Finnair Plus awards',
  [FinnairPlusOptions.MARKETING]: 'Finnair Plus marketing',
  [FinnairPlusOptions.RULES_AND_CONDITIONS]: 'Finnair Plus rules & conditions',
};

export const CategoryMapLevel3 = {
  [OptionalFeedbackInputs.CARE]: 'Care (meals, hotel, phone calls)',
  [OptionalFeedbackInputs.REROUTING]: 'Rerouting/rebooking',
  [OptionalFeedbackInputs.CUSTOMER_SERVICE_ATTITUDE]: 'Customer service attitude and friendliness',
  [OptionalFeedbackInputs.REACHABILITY]: 'Reachability of customer services',
  [OptionalFeedbackInputs.DIGITAL_SERVICES]: 'Digital services',
  [OptionalFeedbackInputs.INFORMATION]: 'Information',
  [OptionalFeedbackInputs.CUSTOMER_COMMUNICATION]: 'Customer communication at airport',
  [OptionalFeedbackInputs.AVAILABILITY_OF_FARES]: 'Availability of fares',
  [OptionalFeedbackInputs.NO_SHOW_OR_RETURN_CANCELLED]: 'Return cancelled due first flight not used',
  [OptionalFeedbackInputs.FARE_RESTRICTIONS]: 'Fare restrictions',
  [OptionalFeedbackInputs.REFUND_POLICY]: 'Refund policy',
  [OptionalFeedbackInputs.WEBSITE_FUNCTIONALITY]: 'Website functionality',
  [OptionalFeedbackInputs.FINNAIR_APP]: 'Finnair app',
  [OptionalFeedbackInputs.CHAT]: 'Chat',
  [OptionalFeedbackInputs.SOCIAL_MEDIA]: 'Social media',
  [OptionalFeedbackInputs.WHATSAPP]: 'WhatsApp',
  [OptionalFeedbackInputs.REACHABILITY_OF_CONTACT_CENTER]: 'Reachability of contact center',
  [OptionalFeedbackInputs.CUSTOMER_SERVICE_SKILLS]: 'Customer service skills',
  [OptionalFeedbackInputs.TRAVEL_AGENCIES]: 'Travel agencies',
  [OptionalFeedbackInputs.OTHER_DISTRIBUTORS]: 'Other distributors',
  [OptionalFeedbackInputs.CHANGES_IN_TRAVEL_PLANS]: 'Changes in travel plans',
  [OptionalFeedbackInputs.CHANGES_NOT_INFORMED]: 'Changes not informed',
  [OptionalFeedbackInputs.LONG_BAGGAGE_DELIVERY_TIME]: 'Long baggage delivery time',
  [OptionalFeedbackInputs.INFORMATION_ANNOUNCEMENTS_SIGNAGE]: 'Information/announcements/signage',
  [OptionalFeedbackInputs.ARRIVAL_SERVICE_REACHABALITY_BY_PHONE]: 'Arrival service reachability by phone',
  [OptionalFeedbackInputs.EXCESS_BAGGAGE_CHARGE]: 'Excess baggage charge',
  [OptionalFeedbackInputs.PRE_PAID_SEATS]: 'Pre-paid seats',
  [OptionalFeedbackInputs.SEATING]: 'Seating',
  [OptionalFeedbackInputs.CHECK_IN_QUEUE]: 'Check-in queue',
  [OptionalFeedbackInputs.RECOGNITION_OF_TOP_TIER_PRIORITY_CUSTOMER]: 'Recognition of top tier/priority customer',
  [OptionalFeedbackInputs.FLUENCY_OF_BOARDING]: 'Fluency of boarding',
  [OptionalFeedbackInputs.CARRY_ON_BAGGAGE]: 'Carry-on baggage',
  [OptionalFeedbackInputs.REFUSED_ACCEPTANCE_AT_THE_BOARDING_GATE]: 'Refused acceptance at the boarding gate',
  [OptionalFeedbackInputs.MISSED_CONNECTING_FLIGHT]: 'Missed connecting flight',
  [OptionalFeedbackInputs.ACCESS_TO_LOUNGE]: 'Access to lounge',
  [OptionalFeedbackInputs.INTERIOR_APPEARANCE_CLEANLINESS]: 'Interior/appearance/cleanliness',
  [OptionalFeedbackInputs.MEAL_AND_BEVERAGE_SERVICE]: 'Meal and beverage service',
  [OptionalFeedbackInputs.INTERNET_CHECK_IN]: 'Internet check-in',
  [OptionalFeedbackInputs.KIOSK_CHECK_IN]: 'Kiosk check-in',
  [OptionalFeedbackInputs.SMS_CHECK_IN]: 'SMS check-in',
  [OptionalFeedbackInputs.INFO_ON_DIGITAL_CHANNEL]: 'Information on digital channel',
  [OptionalFeedbackInputs.TRAVEL_DOCUMENT_ISSUE]: 'Travel document issue',
  [OptionalFeedbackInputs.REFUSED_ACCEPTANCE_AT_CHECK_IN]: 'Refused acceptance at check-in',
  [OptionalFeedbackInputs.CUSTOMS]: 'Customs',
  [OptionalFeedbackInputs.PASSPORT_CONTROL]: 'Passport control',
  [OptionalFeedbackInputs.SECURITY_CHECKS]: 'Security checks',
  [OptionalFeedbackInputs.FACILITIES_OR_SIGNAGE_OR_SHOPS]: 'Facilities/signage/shops',
  [OptionalFeedbackInputs.FLY_RAIL_TRAIN_CONNECTION_IN_GERMANY]: 'Fly&Rail train connection (in Germany)',
  [OptionalFeedbackInputs.INJURY_HAPPENED_INSIDE_AIRPORT_TERMINAL]: 'Injury happened inside airport terminal',
  [OptionalFeedbackInputs.INJURY_HAPPENED_WHEN_BOARDING_EXITING_THE_AIRCRAFT]:
    'Injury happened when boarding/exiting the aircraft',
  [OptionalFeedbackInputs.DAMAGE_SPILLAGE_CAUSED_TO_CLOTHING_OR_BELONGINGS]:
    'Damage/spillage/injury caused to clothing/belongings/on person',
  [OptionalFeedbackInputs.PILOTS]: 'Pilots',
  [OptionalFeedbackInputs.COMPLIMENTARY_MEALS]: 'Complimentary meals',
  [OptionalFeedbackInputs.SPECIAL_MEALS]: 'Special meals',
  [OptionalFeedbackInputs.FOOD_AND_DRINK_BOUGHT_ONBOARD]: 'Food/drink bought onboard',
  [OptionalFeedbackInputs.FOOD_SAFETY]: 'Food safety',
  [OptionalFeedbackInputs.DEFECTED_WORKING_ONLY_PARTIALLY]: 'Defected/working only partially',
  [OptionalFeedbackInputs.SELECTION_OF_MOVIES_GAMES_MUSIC_NEWSPAPERS]:
    'Selection of movies/ games/ music/ newspapers etc',
  [OptionalFeedbackInputs.DEFECTIVE_SEAT]: 'Defected seat',
  [OptionalFeedbackInputs.DEFECTIVE_TABLE_OR_READING_LIGHT_ETC]: 'Defective table/reading light etc.',
  [OptionalFeedbackInputs.INTERNET_NOT_WORKING]: 'Not functioning',
  [OptionalFeedbackInputs.CLEANLINESS_AND_HYGIENE]: 'Cleanliness/hygiene',
  [OptionalFeedbackInputs.HEATING_VENTILATION_NOISE_LIGHTS]: 'Heating/ventilation/noise/lights',
  [OptionalFeedbackInputs.AIRCRAFT_TYPE_CODE_SHARE]: 'Aircraft type/code share',
  [OptionalFeedbackInputs.HAND_BAGGAGE_ISSUES]: 'Hand baggage issues',
  [OptionalFeedbackInputs.DISTURBING_FELLOW_PASSENGER]: 'Disturbing fellow customer',
  [OptionalFeedbackInputs.AVAILABILITY]: 'Availability',
  [OptionalFeedbackInputs.SEAT_CHANGED]: 'Seat changed',
  [OptionalFeedbackInputs.BOOKING_PROCESS]: 'Booking process',
  [OptionalFeedbackInputs.PRICE]: 'Price',
  [OptionalFeedbackInputs.SELECTION]: 'Selection',
  [OptionalFeedbackInputs.MISSING_OR_CHANGED_MEAL]: 'Missing or changed',
  [OptionalFeedbackInputs.QUALITY]: 'Quality',
  [OptionalFeedbackInputs.WIFI_NOT_WORKING]: 'Wifi not working',
  [OptionalFeedbackInputs.ITEM_MISSING_OR_BROKEN]: 'Item missing or broken',
  [OptionalFeedbackInputs.INFLIGHT_PREORDER_SALES]: 'Inflight/preorder sales',
  [OptionalFeedbackInputs.BOOKING_EXPERIENCE]: 'Booking experience',
  [OptionalFeedbackInputs.AIRPORT_EXPERIENCE]: 'Airport experience',
  [OptionalFeedbackInputs.INFLIGHT_EXPERIENCE]: 'Inflight experience',
  [OptionalFeedbackInputs.DAMAGED_MOBILITY_AID]: 'Damaged mobility aid',
  [OptionalFeedbackInputs.DELAYED_MOBILITY_AID]: 'Delayed mobility aid',
  [OptionalFeedbackInputs.MOBILITY_AID_HANDLING]: 'Mobility aid handling',
  [OptionalFeedbackInputs.DELAYED_DELIVERY_OF_PET]: 'Delayed delivery of pet carrier (AVIH)',
  [OptionalFeedbackInputs.DAMAGED_PET_CARRIER]: 'Damaged pet carrier (AVIH)',
  [OptionalFeedbackInputs.WEB_PAGES_FUNCTIONALITY_INFO]: 'Web pages functionality/info',
  [OptionalFeedbackInputs.FUNCTIONALITY_OF_PROCEDURES]: 'Functionality of procedures',
  [OptionalFeedbackInputs.REACHABILITY_OF_SERVICE]: 'Reachability of service',
  [OptionalFeedbackInputs.TIER_MEMBER_BENEFITS]: 'Tier member benefits',
  [OptionalFeedbackInputs.BASIC_MEMBER_BENEFITS]: 'Basic member benefits',
  [OptionalFeedbackInputs.JUNIOR_PLUS_BENEFITS]: 'Junior plus benefits',
  [OptionalFeedbackInputs.UPGRADES]: 'Upgrade (all)',
  [OptionalFeedbackInputs.FLIGHT_AWARD_AVAILABILITY]: 'Flight award availability',
  [OptionalFeedbackInputs.FINNAIR_FLIGHT_AWARD]: 'Finnair flight award',
  [OptionalFeedbackInputs.PARTNER_FLIGHT_AWARD]: 'Partner flight award',
  [OptionalFeedbackInputs.NON_AIRLINE_AWARD]: 'Non-airline award',
  [OptionalFeedbackInputs.CAMPAIGN]: 'Campaign',
  [OptionalFeedbackInputs.EMAIL_NEWSLETTERS]: 'Email newsletters',
  [OptionalFeedbackInputs.PRINTED_MATERIAL]: 'Printed material',
  [OptionalFeedbackInputs.MEMBERSHIP_CARDS]: 'Membership cards',
  [OptionalFeedbackInputs.TIER_QUALIFICATION]: 'Tier qualification',
  [OptionalFeedbackInputs.EXPIRATION_OF_POINTS]: 'The expiration of the points',
  [OptionalFeedbackInputs.COMPARISON_TO_COMPETITOR_PROGRAM]: 'Comparison to competitor program',
  [OptionalFeedbackInputs.JOINING_FINNAIR_PLUS]: 'Joining Finnair Plus',
};

export interface MultiStepFormStepLogic {
  nextStep: MultiStepFormSteps | undefined;
  currentStep: MultiStepFormSteps | undefined;
}

export interface LinkAttachmentToCase {
  caseId: string;
  lastName: string;
  attachmentId: string;
}

export interface LinkAttachmentToCaseData {
  attachmentIds: string[];
  caseId: string;
  lastName: string;
}

export interface LinkAttachmentToCaseResponse {
  id: string;
}

export interface UploadCaseAttachment {
  caseId: string;
  lastName: string;
  file: File;
}
